import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { MapPin, Shield, X, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

const LocationVerifyModal = ({ onVerified, onBlocked }) => {
  const { token } = useAuth();
  const [phase, setPhase] = useState("prompt"); // prompt | checking | denied | blocked | error
  const [stateInfo, setStateInfo] = useState(null);

  // Check if already verified this session
  useEffect(() => {
    const verified = sessionStorage.getItem("jr_location_verified");
    if (verified === "true") {
      onVerified?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // IP-based fallback — used when GPS is denied/unavailable
  const verifyViaIP = async () => {
    setPhase("checking");
    try {
      const { data } = await axios.get(`${API}/geo/check`);
      if (data.allowed) {
        sessionStorage.setItem("jr_location_verified", "true");
        sessionStorage.setItem("jr_geo_method", "ip");
        if (data.state) sessionStorage.setItem("jr_gps_state", data.state);
        onVerified?.();
      } else {
        setStateInfo(data);
        setPhase("blocked");
      }
    } catch {
      // If even IP-check fails, fail open (don't strand legit users on infra errors)
      sessionStorage.setItem("jr_location_verified", "true");
      onVerified?.();
    }
  };

  const requestLocation = () => {
    if (!navigator.geolocation) {
      // No geolocation support → fall back to IP-based
      verifyViaIP();
      return;
    }

    setPhase("checking");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { data } = await axios.post(`${API}/geo/verify-gps`, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });

          if (data.allowed) {
            sessionStorage.setItem("jr_location_verified", "true");
            sessionStorage.setItem("jr_geo_method", "gps");
            sessionStorage.setItem("jr_gps_state", data.state || "");
            onVerified?.();
          } else {
            setStateInfo(data);
            setPhase("blocked");
          }
        } catch {
          // GPS API hiccup → try IP-based fallback rather than block legit users
          verifyViaIP();
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          // User denied GPS → quietly fall back to IP-based verification
          verifyViaIP();
        } else {
          // Timeout / unavailable → also fall back to IP
          verifyViaIP();
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Already verified
  if (sessionStorage.getItem("jr_location_verified") === "true") return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="location-verify-modal">
      <div className="bg-[#0f1529] border border-white/10 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-3">
            <MapPin className="w-8 h-8 text-blue-400" />
          </div>
          <h2 className="text-xl font-bold text-white">Location Verification</h2>
          <p className="text-zinc-400 text-sm mt-1">Required for legal compliance</p>
        </div>

        {/* Body */}
        <div className="p-6">

          {/* Prompt Phase */}
          {phase === "prompt" && (
            <>
              <p className="text-zinc-300 text-sm mb-4 text-center">
                JackRoyals is required by law to verify you are located in an eligible U.S. state. 
                Please allow location access to continue.
              </p>
              <div className="space-y-2 mb-6">
                {[
                  "Your location is only used for compliance verification",
                  "We do not store or track your GPS data",
                  "Required each session for your protection",
                ].map((text, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="text-xs text-zinc-400">{text}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={requestLocation}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold h-12 rounded-xl text-base"
                data-testid="allow-location-btn"
              >
                <MapPin className="w-5 h-5 mr-2" />
                Allow
              </Button>
              <Button
                onClick={verifyViaIP}
                variant="ghost"
                className="w-full text-zinc-400 hover:text-white hover:bg-white/5 font-bold h-11 rounded-xl text-sm mt-2"
                data-testid="deny-location-btn"
              >
                Use IP-based verification instead
              </Button>
            </>
          )}

          {/* Checking Phase */}
          {phase === "checking" && (
            <div className="text-center py-4">
              <Loader2 className="w-10 h-10 text-blue-400 animate-spin mx-auto mb-3" />
              <p className="text-white font-medium">Verifying your location...</p>
              <p className="text-zinc-500 text-xs mt-1">This only takes a moment</p>
            </div>
          )}

          {/* Denied Phase */}
          {phase === "denied" && (
            <>
              <div className="text-center mb-4">
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white">Location Required</h3>
                <p className="text-zinc-400 text-sm mt-2">
                  Location access was denied. JackRoyals requires location verification 
                  to comply with U.S. sweepstakes regulations. Please enable location 
                  in your browser settings and try again.
                </p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4">
                <p className="text-xs text-amber-300">
                  <strong>How to enable:</strong> Click the lock/location icon in your browser's address bar 
                  and set Location to "Allow", then refresh the page.
                </p>
              </div>
              <Button
                onClick={() => setPhase("prompt")}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold h-11 rounded-xl"
                data-testid="retry-location-btn"
              >
                Try Again
              </Button>
            </>
          )}

          {/* Blocked Phase */}
          {phase === "blocked" && (
            <>
              <div className="text-center mb-4">
                <X className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white">Access Restricted</h3>
                <p className="text-zinc-400 text-sm mt-2">
                  {stateInfo?.reason || `JackRoyals is not available in ${stateInfo?.state_name || "your location"}.`}
                </p>
              </div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
                <p className="text-xs text-red-300">
                  Use of VPN, proxy, or location-masking tools to bypass geographic restrictions 
                  violates our Terms of Service and may result in account termination.
                </p>
              </div>
              <Button
                onClick={() => onBlocked?.()}
                className="w-full bg-zinc-700 hover:bg-zinc-600 text-white font-bold h-11 rounded-xl"
                data-testid="location-blocked-ok-btn"
              >
                I Understand
              </Button>
            </>
          )}

          {/* Error Phase */}
          {phase === "error" && (
            <>
              <div className="text-center mb-4">
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white">Location Unavailable</h3>
                <p className="text-zinc-400 text-sm mt-2">
                  We couldn't determine your location. This may be due to your browser settings 
                  or device permissions. Please enable location services and try again.
                </p>
              </div>
              <Button
                onClick={() => setPhase("prompt")}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold h-11 rounded-xl"
              >
                Try Again
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationVerifyModal;
